import { useState, useEffect, useRef, useCallback } from "react";
import { t } from "i18n";
import betaAPI from "v2/api";
import { formatApiError } from "v2/views/components/Manufacturing/constants";
import { parseFactoryLines } from "utils/factoryTest";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const normMac = (v) =>
  String(v || "")
    .replace(/[^0-9a-fA-F]/g, "")
    .toUpperCase();

/**
 * Identify the connected wearable by MAC (prefer the status/banner MAC, else the
 * `id` command) and load its manufacturing record from the API. Returns the
 * record plus a state machine and refresh helpers, so both the top bar and the
 * workbench body can share one source of truth.
 *
 * @param {string|null} deviceMac  MAC from devCfg (status/banner), if known
 * @param {object} opts  { connected, send, kick, receivedData, onRegistered }
 */
export function useDeviceRecord(deviceMac, opts = {}) {
  const { connected, send, kick, receivedData, onRegistered } = opts;

  const [mac, setMac] = useState(deviceMac ? normMac(deviceMac) : null);
  const [wearable, setWearable] = useState(null);
  const [recordStatus, setRecordStatus] = useState("identifying"); // identifying | ready | notfound | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const macRef = useRef(mac);
  useEffect(() => {
    macRef.current = mac;
  }, [mac]);
  const scanPosRef = useRef((receivedData || []).length);
  const idSentRef = useRef(false);

  // Adopt the banner/status MAC as soon as it surfaces.
  useEffect(() => {
    if (!deviceMac) return;
    const m = normMac(deviceMac);
    if (m && m !== macRef.current) setMac(m);
  }, [deviceMac]);

  // Fallback identify: no MAC shortly after connect → ask the device with `id`.
  useEffect(() => {
    if (mac || !connected) return;
    let cancelled = false;
    (async () => {
      await sleep(2500);
      if (cancelled || macRef.current || idSentRef.current) return;
      idSentRef.current = true;
      scanPosRef.current = (receivedData || []).length;
      await send?.("id").catch?.(() => {});
      for (let i = 0; i < 6 && !cancelled; i++) {
        await sleep(1500);
        if (cancelled || macRef.current) break;
        await kick?.().catch?.(() => {});
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [mac, connected]);

  // Scan the stream for an `id` line (fallback path only).
  useEffect(() => {
    if (macRef.current || !idSentRef.current) return;
    const rd = receivedData || [];
    if (rd.length <= scanPosRef.current) return;
    for (let i = scanPosRef.current; i < rd.length; i++) {
      const hit = parseFactoryLines(rd[i]?.data).find(
        (ev) => ev.t === "id" && ev.mac
      );
      if (hit) {
        setMac(normMac(hit.mac));
        break;
      }
    }
    scanPosRef.current = rd.length;
  }, [receivedData]);

  const fetchRecord = useCallback(async () => {
    if (!macRef.current) return;
    try {
      const res = await betaAPI.getWearable({ bleMac: macRef.current });
      setWearable(res.data);
      setRecordStatus("ready");
      onRegistered?.(true);
    } catch (err) {
      if (err?.status === 404) {
        setWearable(null);
        setRecordStatus("notfound");
        onRegistered?.(false);
      } else {
        setErrorMsg(formatApiError(err, t("utils.deviceRecord.loadFailed")));
        setRecordStatus("error");
      }
    }
    // eslint-disable-next-line
  }, []);

  // Look the device up once the MAC is known.
  useEffect(() => {
    if (!mac) return;
    setRecordStatus("identifying");
    fetchRecord();
    // eslint-disable-next-line
  }, [mac]);

  // Step attempts and status changes both return the full updated detail — swap
  // it in wholesale and bump the key so the audit trail refetches.
  const handleUpdated = useCallback((updated) => {
    if (updated) setWearable(updated);
    setHistoryKey((k) => k + 1);
  }, []);

  const refresh = useCallback(async () => {
    if (!macRef.current || refreshing) return;
    setRefreshing(true);
    try {
      await fetchRecord();
      setHistoryKey((k) => k + 1);
    } finally {
      setRefreshing(false);
    }
  }, [fetchRecord, refreshing]);

  return {
    mac,
    wearable,
    recordStatus,
    errorMsg,
    historyKey,
    refreshing,
    handleUpdated,
    refresh,
  };
}
